import { ModalViewModel } from '@/infra/ModalViewModel'
import { computed } from 'mobx'
import { type WorkspaceOpportunityInvitationStore } from './WorkspaceOpportunityInvitationStore'
import { task } from 'mobx-task'
import { type WorkspaceInvitation } from './WorkspaceInvitation'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * View Model for the Workspace Invitation Modal
 */
export class WorkspaceInvitationModalViewModel {
  readonly modal = new ModalViewModel()

  constructor(
    private readonly _workspaceOpportunityInvitationStore: WorkspaceOpportunityInvitationStore,
    private readonly _workspaceOpportunity: WorkspaceInvitation,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Id of the Workspace Posting
   */
  @computed
  get id() {
    return this._workspaceOpportunity.id
  }

  /**
   * The date, expressed in milliseconds, at which the invitation expires
   */
  @computed
  get expirationMillis() {
    return this._workspaceOpportunity.dateExpires.getTime()
  }

  /**
   * The time until the invitation expires
   */
  @computed
  get timeUntilExpiration() {
    return this._workspaceOpportunity.timeUntilExpiration ?? 0
  }

  /**
   * Formatted string of the software requirements for the Workspace Opportunity
   */
  @computed
  get software() {
    if (this._workspaceOpportunity.supplementalSoftware) {
      return `${this._workspaceOpportunity.primarySoftware}, ${this._workspaceOpportunity.supplementalSoftware}`
    }
    return `${this._workspaceOpportunity.primarySoftware}`
  }

  /**
   * The designated location for the Workspace Opportunity
   */
  @computed
  get location() {
    return this._workspaceOpportunity.location
  }

  /**
   * The description of the Workspace Opportunity
   */
  @computed
  get description() {
    return this._workspaceOpportunity.description
  }

  /**
   * The specialties for the Workspace Opportunity, as one line.
   */
  @computed
  get specs() {
    return this._workspaceOpportunity.specialties.join(' • ')
  }

  /**
   * Accepts the invitation.
   */
  accept = task.resolved(async () => {
    await this._workspaceOpportunityInvitationStore
      .acceptInvitation(this._workspaceOpportunity.id)
      .catch(this._createAlertErrorHandler('Accepting the invitation failed'))
    this.modal.close()
  })

  /**
   * Rejects the invitation.
   */
  reject = task.resolved(async () => {
    await this._workspaceOpportunityInvitationStore
      .rejectInvitation(this._workspaceOpportunity.id)
      .catch(this._createAlertErrorHandler('Rejecting the invitation failed'))
    this.modal.close()
  })
}
